<script setup>
import { h, inject, isVNode, normalizeClass, useAttrs } from 'vue';
import FontIcon from './FontIcon.vue';

const props = defineProps({
  icon: { type: [String, Object, Function], required: true },
});
const attrs = useAttrs();

const fontIcons = new Map([
  ...[
    'edit',
    'i-lucide-pencil',
    'i-lucide-pencil-line',
    'i-lucide-pen',
    'i-lucide-pen-line',
    'i-lucide-square-pen',
    'i-woot-edit-pen',
  ].map(name => [name, 'pencil-edit-outline']),
  ['i-lucide-external-link', 'external-link-outline'],
  ['i-lucide-refresh-cw', 'refresh-outline'],
  ['i-lucide-refresh-ccw', 'refresh-outline'],
  ['refresh-outline', 'refresh-outline'],
  ['refresh-filled', 'refresh-filled'],
  ['thumbs-up-outline', 'thumbs-up-outline'],
  ['thumbs-up-filled', 'thumbs-up-filled'],
  ['thumbs-down-outline', 'thumbs-down-outline'],
  ['thumbs-down-filled', 'thumbs-down-filled'],
  ['delete', 'delete-outline'],
  ['i-lucide-trash', 'delete-outline'],
  ['i-lucide-trash-2', 'delete-outline'],
  ...inject('customFontIcons', []),
]);

const xIconSize = classes => {
  const size = classes.match(/\bsize-(\d+(?:\.\d+)?|\[\d+px\])(?=\s|$)/);
  const width = classes.match(/\bw-(\d+(?:\.\d+)?|\[\d+px\])(?=\s|$)/);
  const value = size?.[1] || width?.[1] || '4';
  if (value.startsWith('[')) return Number.parseInt(value.slice(1), 10);
  return Number(value) * 4;
};

const renderIcon = () => {
  if (!props.icon) return null;
  if (typeof props.icon === 'function' || isVNode(props.icon)) {
    return props.icon;
  }
  if (typeof props.icon === 'string') {
    const classes = props.icon.split(/\s+/);
    const iconName = classes.find(name => fontIcons.has(name));
    if (iconName) {
      const allClasses = `${props.icon} ${normalizeClass(attrs.class)}`;
      return h(FontIcon, {
        icon: fontIcons.get(iconName),
        size: /\b(?:size|w)-/.test(allClasses) ? xIconSize(allClasses) : null,
        class: [
          'leading-none',
          ...classes.filter(name => !fontIcons.has(name)),
        ],
      });
    }
  }
  if (
    typeof props.icon === 'string' &&
    /(?:^|\s)i-lucide-x(?:\s|$)/.test(props.icon)
  ) {
    const classes = `${props.icon} ${normalizeClass(attrs.class)}`;
    return h(FontIcon, {
      icon: 'x',
      size: xIconSize(classes),
      class: props.icon.replace(/(?:^|\s)i-lucide-x(?=\s|$)/, ''),
    });
  }
  if (
    typeof props.icon === 'string' &&
    /(?:^|\s)i-lucide-alarm-clock(?:\s|$)/.test(props.icon)
  ) {
    const classes = `${props.icon} ${normalizeClass(attrs.class)}`;
    return h(FontIcon, {
      icon: 'alarm-outline',
      size: xIconSize(classes),
      class: props.icon.replace(/(?:^|\s)i-lucide-alarm-clock(?=\s|$)/, ''),
    });
  }
  return h('span', { class: props.icon });
};
</script>

<template>
  <component :is="renderIcon" />
</template>
